import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, MagnifyingGlass, PencilSimple, Trash, Car } from "@phosphor-icons/react";
import { useAuth, can } from "@/context/AuthContext";

const empty = { name: "", phone: "", email: "", notes: "" };

export default function Clients() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = can(user, "vehicles");

  const load = () => api.get("/clients", { params: { search } }).then((r) => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search]);

  const save = async () => {
    if (!form.name) return toast.error("Вкажіть імʼя клієнта");
    try {
      if (editId) await api.put(`/clients/${editId}`, form);
      else await api.post("/clients", form);
      toast.success("Збережено"); setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const edit = (c) => { setForm({ name: c.name, phone: c.phone, email: c.email, notes: c.notes || "" }); setEditId(c.id); setOpen(true); };
  const del = async (id) => { if (!window.confirm("Видалити клієнта?")) return; await api.delete(`/clients/${id}`); toast.success("Видалено"); load(); };

  return (
    <div>
      <PageHeader title="Клієнти" subtitle={`Всього: ${list.length}`}
        action={canEdit && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
            <DialogTrigger asChild><Button data-testid="add-client-btn"><Plus size={18} className="mr-1" /> Клієнт</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editId ? "Редагувати клієнта" : "Новий клієнт"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Імʼя *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" className="mt-1.5" /></div>
                <div><Label>Телефон</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone" className="mt-1.5" /></div>
                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email" className="mt-1.5" /></div>
                <div><Label>Нотатки</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
              </div>
              <DialogFooter><Button onClick={save} data-testid="save-client-btn">Зберегти</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="relative mb-4 max-w-sm">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Пошук клієнта..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="clients-search" />
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Клієнт</TableHead><TableHead>Телефон</TableHead><TableHead>Email</TableHead>
              <TableHead>Авто</TableHead><TableHead className="text-right">Дії</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((c) => (
              <TableRow key={c.id} className="cursor-pointer" data-testid={`client-row-${c.id}`}>
                <TableCell className="font-semibold" onClick={() => navigate(`/clients/${c.id}`)}>{c.name}</TableCell>
                <TableCell onClick={() => navigate(`/clients/${c.id}`)}>{c.phone || "—"}</TableCell>
                <TableCell onClick={() => navigate(`/clients/${c.id}`)}>{c.email || "—"}</TableCell>
                <TableCell onClick={() => navigate(`/clients/${c.id}`)}>
                  <span className="inline-flex items-center gap-1 text-muted-foreground"><Car size={16} /> {c.vehicles_count}</span>
                </TableCell>
                <TableCell className="text-right">
                  {canEdit && <>
                    <button className="text-muted-foreground hover:text-foreground p-1" onClick={() => edit(c)} data-testid={`edit-client-${c.id}`}><PencilSimple size={18} /></button>
                    <button className="text-muted-foreground hover:text-destructive p-1" onClick={() => del(c.id)} data-testid={`del-client-${c.id}`}><Trash size={18} /></button>
                  </>}
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Немає клієнтів</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
